// Datadog names a click from the clicked element's text. Here that text is translated (Turkish on
// secure.flexigo.com, English elsewhere) and is often data: rider and driver names, plates, emails.
// Before a click leaves the browser its name is rebuilt so it is stable across languages and free of
// personal data:
//  - a name Datadog took from `data-dd-action-name` (on the element or an ancestor) is kept;
//  - AG Grid clicks become `grid:<grid>:<column>` (`grid:<grid>:header:<column>` on the header);
//  - SweetAlert buttons become `dialog:confirm|deny|cancel|close`;
//  - text that is a UI string from the i18n catalogue becomes `<Component>: <English string>`;
//  - anything else (data, numbers, free text) keeps only the component: `<Component>`.

const SWAL_BUTTON_RE = /\bswal2-(confirm|deny|cancel|close)\b/;
const CONTROL_SELECTOR = 'button, a, input, select, label, [role="button"]';

function normalize(text) {
  return typeof text === 'string' ? text.replace(/\s+/g, ' ').trim() : '';
}

/**
 * Maps every UI string of the active locale to its English version.
 * @param {object} messages vue-i18n messages ({ tr: {...}, en: {...} })
 * @param {string} locale
 * @returns {Map<string, string>}
 */
export function buildEnglishByText(messages, locale) {
  const englishByText = new Map();
  const walk = (local, english) => {
    Object.keys(local || {}).forEach((key) => {
      const value = local[key];
      const englishValue = english?.[key];
      if (typeof value === 'string') {
        const text = normalize(value);
        if (text && typeof englishValue === 'string' && !englishByText.has(text)) {
          englishByText.set(text, normalize(englishValue));
        }
      } else if (value && typeof value === 'object') {
        walk(value, englishValue);
      }
    });
  };
  walk(messages?.[locale], messages?.en);
  return englishByText;
}

// Name of the Vue component the element was clicked in. A wrapper control's own root (a button
// component, say) names the component that uses it instead.
function componentName(element) {
  let el = element;
  // eslint-disable-next-line no-underscore-dangle
  while (el && !el.__vue__) el = el.parentElement;
  // eslint-disable-next-line no-underscore-dangle
  let vm = el?.__vue__;
  if (vm?.$parent && el.matches(CONTROL_SELECTOR)) vm = vm.$parent;
  return vm?.$options.name || 'unknown';
}

// What the name of a click on this element is built from: a fixed name, or the component it was
// clicked in.
function describeClickTarget(target) {
  if (!target?.closest) return { component: 'unknown' };

  const grid = target.closest('[data-dd-grid]');
  if (grid) {
    const column = target.closest('[col-id]')?.getAttribute('col-id');
    const part = target.closest('.ag-header') ? 'header:' : '';
    return { name: `grid:${grid.getAttribute('data-dd-grid')}:${column ? `${part}${column}` : 'other'}` };
  }

  if (target.closest('.swal2-container')) {
    const button = target.closest('button')?.className.match(SWAL_BUTTON_RE);
    return { name: `dialog:${button ? button[1] : 'other'}` };
  }

  return { component: componentName(target) };
}

// Datadog names a click from the element pressed (pointerdown) but hands this hook the element
// released on (pointerup), and only when the action ends, often after the element has left the DOM
// (a panel that closed, a re-rendered map). So the pressed element is described while the click
// happens, and the description is filed under the released element: `rememberClickTarget` runs as
// a capture listener for both events.
const clickTargets = new WeakMap();
let pressed = null;

/**
 * @param {PointerEvent} event a pointerdown or pointerup event
 */
export function rememberClickTarget(event) {
  const { target, type } = event;
  if (!target?.closest) return;
  if (type === 'pointerdown') {
    pressed = describeClickTarget(target);
    clickTargets.set(target, pressed);
  } else {
    clickTargets.set(target, pressed || describeClickTarget(target));
    pressed = null;
  }
}

/**
 * @param {Element|null} target the element of the click event Datadog passes (pointerup)
 * @param {string} datadogName the name Datadog computed from the pressed element
 * @param {Map<string, string>} englishByText see `buildEnglishByText`
 * @param {string} [nameSource] Datadog's `_dd.action.name_source`
 * @returns {string|null} the name to send, or null to keep Datadog's: only a `data-dd-action-name`
 */
export function clickActionName(target, datadogName, englishByText, nameSource) {
  if (nameSource === 'custom_attribute') return null;
  const description = (target && clickTargets.get(target)) || describeClickTarget(target);
  if (description.name) return description.name;
  const english = englishByText.get(normalize(datadogName));
  return english ? `${description.component}: ${english}` : description.component;
}
